
import React, { useMemo } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Eye, ChevronRight, Trash2, RefreshCw } from "lucide-react";
import { format } from "date-fns";
import { useLocalization } from '../providers/LocalizationProvider';
import { determineDeliveryStatus } from './utils';

const statusColors = {
  ordered: 'bg-blue-100 text-blue-800 border-blue-200',
  paid: 'bg-green-100 text-green-800 border-green-200',
  signed: 'bg-purple-100 text-purple-800 border-purple-200',
  packed: 'bg-orange-100 text-orange-800 border-orange-200',
  shipped: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  delivered: 'bg-emerald-100 text-emerald-800 border-emerald-200'
};

export default function OrderTable({
  orders, onOrderSelect, onOrderDelete, onOrderRestore, showDeleted, isLoading }) {
  const { currencySymbol, t } = useLocalization();

  // Мемоизируем статусы доставки для избежания лишних вычислений
  const deliveryStatuses = useMemo(() => {
    return orders.reduce((acc, order) => {
      acc[order.id] = determineDeliveryStatus(order);
      return acc;
    }, {});
  }, [orders]);

  if (isLoading) {
    return (
      <Card className="elegant-card">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                <TableHead>{t('orders.table.number')}</TableHead>
                <TableHead>{t('orders.table.customer')}</TableHead>
                <TableHead>{t('orders.table.date')}</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>{t('orders.table.status')}</TableHead>
                <TableHead>{t('orders.table.delivery')}</TableHead>
                <TableHead>{t('orders.table.total')}</TableHead>
                <TableHead>{t('actions.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array(5).fill(0).map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-6 w-20 rounded-full" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-4" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                  <TableCell><Skeleton className="h-8 w-8 rounded" /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="elegant-card overflow-hidden">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="font-bold text-slate-900">{t('orders.table.number')}</TableHead>
              <TableHead className="font-bold text-slate-900">{t('orders.table.customer')}</TableHead>
              <TableHead className="font-bold text-slate-900">{t('orders.table.date')}</TableHead>
              <TableHead className="font-bold text-slate-900">Items</TableHead>
              <TableHead className="font-bold text-slate-900">{t('orders.table.status')}</TableHead>
              <TableHead className="font-bold text-slate-900">{t('orders.table.delivery')}</TableHead>
              <TableHead className="font-bold text-slate-900">{t('orders.table.total')}</TableHead>
              <TableHead className="font-bold text-slate-900">{t('actions.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                  <div className="flex flex-col items-center gap-2">
                    <Eye className="w-12 h-12 text-slate-300" />
                    <p className="font-medium">{t('orders.table.no_data')}</p>
                    <p className="text-sm">Orders matching your criteria will appear here</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow 
                  key={order.id} 
                  className={`transition-colors ${
                    order.is_deleted 
                      ? 'bg-red-50/50 opacity-75' 
                      : 'hover:bg-slate-50/50 cursor-pointer'
                  }`}
                  onClick={() => !order.is_deleted && onOrderSelect(order)}
                >
                  <TableCell className={`font-medium ${order.is_deleted ? 'text-red-600' : 'text-slate-900'}`}>
                    #{order.order_number || order.id.slice(-6)}
                    {order.is_deleted && <span className="text-xs ml-1">(Удален)</span>}
                  </TableCell>
                  <TableCell>
                    <div>
                      <p className="font-medium text-slate-900">{order.customer_name}</p>
                      <p className="text-sm text-slate-500">{order.customer_email}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600">
                    {format(new Date(order.created_date), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell className="font-medium text-slate-900">
                    {order.items?.reduce((sum, item) => sum + (item.quantity || 0), 0) || 0}
                  </TableCell>
                  <TableCell>
                    <Badge className={`${statusColors[order.status]} border font-medium`}>
                      {t(`order_statuses.${order.status}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const deliveryStatus = deliveryStatuses[order.id];
                      return deliveryStatus ? (
                        <div 
                          className={`${deliveryStatus.color} cursor-help`}
                          title={t(`delivery_status.tooltip.${deliveryStatus.status}`)}
                        >
                          <deliveryStatus.icon className="w-4 h-4" />
                        </div>
                      ) : null;
                    })()}
                  </TableCell>
                  <TableCell className="font-bold text-slate-900">
                    {currencySymbol}{Number(order.total_amount || 0).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {showDeleted && order.is_deleted ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onOrderRestore(order.id);
                          }}
                          className="text-green-600 hover:text-green-800 hover:bg-green-50"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      ) : !order.is_deleted ? (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm('Удалить заказ?')) {
                                onOrderDelete(order.id);
                              }
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOrderSelect(order);
                            }}
                            className="hover:bg-amber-50 hover:text-amber-700"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
